import { useOAuthPkceFlow } from "@solomon-labs/oauth-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { OAuthConfig } from "../../services/orgAuthService";
import {
    authorizeLogin,
    ensureValidToken,
    exchangeCode,
    fetchMe,
    revokeToken,
    startExternalLogin,
    startSsoLogin
} from "../../services/orgAuthService";
import { useOrgAuthStore } from "../../stores/useOrgAuthStore";
import type { MeResponse } from "../../types/orgAuth";

type OrgLoginCredentials =
    | { kind: "password"; email: string; password: string }
    | { kind: "external"; provider: "google"; loginHint?: string }
    | { kind: "sso"; loginHint?: string };

function validateLoginNavigation(config: OAuthConfig, credentials: OrgLoginCredentials, navigateTo: string): void {
    const parsedNavigate = new URL(navigateTo);

    if (credentials.kind === "password") {
        const parsedApiBase = new URL(config.apiBase);
        if (parsedNavigate.origin !== parsedApiBase.origin) {
            throw new Error("Unexpected redirect hostname — possible response tampering");
        }
        return;
    }

    if (
        credentials.kind === "external" &&
        credentials.provider === "google" &&
        parsedNavigate.origin !== "https://accounts.google.com"
    ) {
        throw new Error("Unexpected Google redirect hostname — possible response tampering");
    }
    if (credentials.kind === "sso") {
        const isLoopback = ["localhost", "127.0.0.1", "::1"].includes(parsedNavigate.hostname);
        if (parsedNavigate.protocol !== "https:" && !(parsedNavigate.protocol === "http:" && isLoopback)) {
            throw new Error("Unexpected SSO redirect protocol — possible response tampering");
        }
    }
}

export function needsOrgProfileRefresh(me: MeResponse | null): boolean {
    if (!me) return true;

    const profile = me as Partial<MeResponse>;
    return (
        !("issuerOrg" in profile) ||
        !("issuerCapability" in profile) ||
        !("canActAsIssuer" in profile) ||
        !("issuerCapabilityStatus" in profile) ||
        !Array.isArray(profile.availableClientContexts) ||
        !("defaultClientContextUuid" in profile)
    );
}

export interface UseOrgAuthReturn {
    login: (email: string, password: string) => Promise<void>;
    loginWithGoogle: (loginHint?: string) => Promise<void>;
    loginWithSso: (loginHint?: string) => Promise<void>;
    logout: () => Promise<void>;
    handleCallback: (code: string, state: string) => Promise<void>;
    /** Returns a valid access token, refreshing silently if near expiry. Redirects to /login if refresh fails. */
    getAccessToken: () => Promise<string | null>;
    isLoggingIn: boolean;
    loginError: string | null;
}

/**
 * Orchestrates the full OAuth 2.0 + PKCE login flow for org consoles.
 *
 * Pass the per-app OAuthConfig (client_id, redirect_uri, etc.) as the
 * only argument. This hook is reusable across yaas-issuer and yaas-client.
 */
export function useOrgAuth(config: OAuthConfig): UseOrgAuthReturn {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const tokens = useOrgAuthStore((s) => s.tokens);
    const me = useOrgAuthStore((s) => s.me);
    const { setTokens, setMe, clearAuth } = useOrgAuthStore();
    /**
     * Stays true after a successful login handoff until the page unloads.
     * `useOAuthPkceFlow` clears `isLoading` in `finally` right after
     * `window.location.href` is set, which briefly re-enabled the form.
     */
    const [pendingRedirect, setPendingRedirect] = useState(false);

    const pkceFlow = useOAuthPkceFlow<OrgLoginCredentials, Awaited<ReturnType<typeof exchangeCode>>>({
        config: {
            authorizeEndpoint: `${config.apiBase}/api/oauth/authorize`,
            clientId: config.clientId,
            redirectUri: config.redirectUri,
            scope: config.scope
        },
        onLogin: async (credentials, authorizeUrl, { codeChallenge }) => {
            const { navigateTo } = await (async (): Promise<{ navigateTo: string }> => {
                if (credentials.kind === "password") {
                    return authorizeLogin(config, credentials.email, credentials.password, authorizeUrl, codeChallenge);
                }
                if (credentials.kind === "sso") {
                    return startSsoLogin(config, authorizeUrl, codeChallenge, credentials.loginHint);
                }
                return startExternalLogin(
                    config,
                    credentials.provider,
                    authorizeUrl,
                    codeChallenge,
                    credentials.loginHint
                );
            })();

            validateLoginNavigation(config, credentials, navigateTo);

            return { authorizeUrl: navigateTo };
        },
        exchangeCode: async (code, codeVerifier, nonce) => exchangeCode(config, code, codeVerifier, nonce),
        onTokensReceived: async (tokens) => {
            const me = await fetchMe(config.apiBase, tokens.accessToken);
            setTokens(tokens);
            setMe(me);
            navigate("/");
        }
    });

    // Silently refresh on mount if the stored token is near expiry.
    useEffect(() => {
        const tokens = useOrgAuthStore.getState().tokens;
        if (!tokens) return;
        if (tokens.expiresAt - Date.now() > 60_000) return;

        void ensureValidToken(config, tokens)
            .then((result) => {
                if (result.refreshed) setTokens(result.tokens);
            })
            .catch(() => {
                clearAuth();
                navigate("/login");
            });
    }, [config, navigate, clearAuth, setTokens]);

    useEffect(() => {
        if (!tokens || !needsOrgProfileRefresh(me)) return;

        let cancelled = false;
        void ensureValidToken(config, tokens)
            .then(async (result) => {
                if (cancelled) return;
                if (result.refreshed) setTokens(result.tokens);
                const refreshedMe = await fetchMe(config.apiBase, result.accessToken);
                if (!cancelled) setMe(refreshedMe);
            })
            .catch(() => {
                if (cancelled) return;
                clearAuth();
                navigate("/login");
            });

        return (): void => {
            cancelled = true;
        };
    }, [config, tokens, me, setTokens, setMe, clearAuth, navigate]);

    useEffect(() => {
        if (pkceFlow.error) {
            setPendingRedirect(false);
        }
    }, [pkceFlow.error]);

    const startLogin = useCallback(
        async (credentials: OrgLoginCredentials): Promise<void> => {
            setPendingRedirect(true);
            await pkceFlow.login(credentials);
        },
        [pkceFlow]
    );

    const login = useCallback(
        async (email: string, password: string): Promise<void> => {
            await startLogin({ kind: "password", email, password });
        },
        [startLogin]
    );

    const loginWithGoogle = useCallback(
        async (loginHint?: string): Promise<void> => {
            await startLogin({ kind: "external", provider: "google", loginHint });
        },
        [startLogin]
    );

    const loginWithSso = useCallback(
        async (loginHint?: string): Promise<void> => {
            await startLogin({ kind: "sso", loginHint });
        },
        [startLogin]
    );

    const handleCallback = useCallback(
        async (code: string, state: string): Promise<void> => {
            await pkceFlow.handleCallback(code, state);
        },
        [pkceFlow]
    );

    const logout = useCallback(async (): Promise<void> => {
        const tokens = useOrgAuthStore.getState().tokens;
        if (tokens) {
            try {
                await revokeToken(config, tokens.refreshToken);
            } catch {
                // Revocation failed — proceed with local cleanup anyway
            }
        }
        clearAuth();
        queryClient.clear();
        navigate("/login");
    }, [config, navigate, clearAuth, queryClient]);

    const getAccessToken = useCallback(async (): Promise<string | null> => {
        const tokens = useOrgAuthStore.getState().tokens;
        if (!tokens) return null;
        try {
            const result = await ensureValidToken(config, tokens);
            if (result.refreshed) setTokens(result.tokens);
            return result.accessToken;
        } catch {
            clearAuth();
            navigate("/login");
            return null;
        }
    }, [config, setTokens, clearAuth, navigate]);

    return {
        login,
        loginWithGoogle,
        loginWithSso,
        logout,
        handleCallback,
        getAccessToken,
        isLoggingIn: pkceFlow.isLoading || pendingRedirect,
        loginError: pkceFlow.error
    };
}
